<template>
  <vs-popup
    class="popup overflow-hidden"
    button-close-hidden
    :active="active"
    v-bind="$attrs"
    v-on="$listeners"
    ref="parentPopUp"
    @close="$emit('close')"
  >
    <img
      class="vs-popup--close-button"
      :src="require('@/assets/shipo-popup-icon-close.svg')"
      @click="handleClose"
    >
    <div class="w-full mb-4 text-xl font-semibold">
      Edit Data
    </div>
    <div class="overflow-auto">
      <div class="border-b-2 border-dotted border-gray-400 mb-6" />
      <!-- General Information Section -->
      <vs-row vs-w="12">
        <vs-col
          vs-w="12"
          class="mb-3 flex flex-col font-semibold text-base"
          style="color: #6546C3"
        >
          General Information
        </vs-col>
        
        <!-- Left Column -->
        <vs-col
          vs-w="6"
          class="pr-5 mb-3 flex flex-col"
        >
          <span class="mb-3 font-semibold">Trip Number</span>
          <vs-input
            v-model="form.trip_number"
            class="min-w-full"
            :disabled="true"
          />
        </vs-col>
        
        <vs-col
          vs-w="6"
          class="flex flex-col"
        >
          <span class="mb-3 font-semibold">Product ID</span>
          <vs-input
            v-model="form.product_id"
            class="min-w-full"
            :disabled="true"
          />
        </vs-col>

        <vs-col
          vs-w="6"
          class="pr-5 mb-3 flex flex-col"
        >
          <span class="mb-3 font-semibold">Tug Name</span>
          <vs-input
            v-model="form.tug_name"
            class="min-w-full"
            :disabled="true"
          />
        </vs-col>
        
        <vs-col
          vs-w="6"
          class="flex flex-col"
        >
          <span class="mb-3 font-semibold">Barge Name</span>
          <vs-input
            v-model="form.barge_name"
            class="min-w-full"
            :disabled="true"
          />
        </vs-col>

        <vs-col
          vs-w="6"
          class="pr-5 mb-3 flex flex-col"
        >
          <span class="mb-3 font-semibold">Quantity</span>
          <vs-input
            v-model="form.quantity"
            class="min-w-full"
            type="number"
            :disabled="true"
          />
        </vs-col>
        
        <vs-col
          vs-w="6"
          class="flex flex-col"
        >
          <span class="mb-3 font-semibold">Vessel Agent</span>
          <select-with-search
            v-model="form.vessel_agent"
            placeholder="Select Vessel Agent"
            :options="vesselOptions"
            :disabled="isBusy"
          />
        </vs-col>

        <vs-col
          vs-w="6"
          class="pr-5 mb-3 flex flex-col"
        >
          <span class="mb-3 font-semibold">FLF Loading Facility</span>
          <select-with-search
            v-model="form.flf_loading_facility"
            placeholder="Select FLF Loading Facility"
            :options="loadingFacilityOptions"
            :disabled="isBusy"
          />
        </vs-col>
        
        <vs-col
          vs-w="6"
          class="flex flex-col"
        >
          <span class="mb-3 font-semibold">FLF Nominate</span>
          <select-with-search
            v-model="form.flf_nominate"
            placeholder="Select FLF Nominate"
            :options="flfOptions"
            :disabled="isBusy"
          />
        </vs-col>

        <!-- Full Width Field -->
        <vs-col
          vs-w="12"
          class="mb-3 flex flex-col"
        >
          <span class="mb-3 font-semibold">Blending Scheme</span>
          <select-with-search
            v-model="form.blending_scheme"
            placeholder="Select Blending Scheme"
            :options="blendingSchemeOptions"
            :disabled="isStorage"
          />
        </vs-col>
      </vs-row>

      <!-- Blending Details Section -->
      <vs-row
        v-if="form.blending_scheme === 'blending'"
        vs-w="12"
      >
        <vs-col
          vs-w="12"
          class="mb-3 flex flex-col font-semibold text-base"
          style="color: #6546C3"
        >
          Blending Details
        </vs-col>
        
        <!-- Ratio Section -->
        <vs-col
          vs-w="12"
          class="mb-4 flex flex-col"
        >
          <span class="mb-3 font-semibold">Ratio</span>
          <div class="flex items-center gap-2">
            <vs-input
              v-model="form.ratio_first"
              type="number"
              class="w-full"
              style="text-align: center;"
              :disabled="isBusy"
            />
            <span class="text-lg font-semibold">:</span>
            <vs-input
              v-model="form.ratio_second"
              type="number"
              class="w-full"
              :disabled="isBusy"
            />
          </div>
        </vs-col>

        <!-- Blending Table -->
        <vs-col
          vs-w="12"
          class="mb-4"
        >
          <data-table
            :table-header="blendingTableHeaders"
            :table-data="formattedBlendingTableData"
            :hide-search="true"
            :hide-pagination="true"
            body-height="auto"
            style="width: 50rem;"
          >
            <template #action="{ index, row }">
              <div
                v-if="index > 0"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="removeBlendingRow(row)"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
            <template #trip_number="{ row, index }">
              <select-with-search
                v-if="index > 0"
                v-model="row.trip_number"
                placeholder="Select Trip Number"
                :options="getTripNumberOptionsForRow(index)"
                :disabled="isBusy"
                class="min-w-full"
                body-height="100vh"
                @input="populateTripDetailsForRow(index, $event)"
              />
              <span v-else>{{ row.trip_number }}</span>
            </template>
            <template #tug_name="{ row }">
              <span>{{ row.tug_name }}</span>
            </template>
            <template #barge_name="{ row }">
              <span>{{ row.barge_name }}</span>
            </template>
            <template #product="{ row }">
              <span>{{ row.product }}</span>
            </template>
          </data-table>
        </vs-col>

        <!-- Add Blending Button -->
        <vs-col
          vs-w="12"
          class="mb-4"
        >
          <vs-button
            type="filled"
            color="secondary"
            class="flex items-center justify-center w-full"
            @click="addBlendingRow"
            :disabled="isBusy"
          >
            <span class="font-semibold align-top">Add Blending</span>
          </vs-button>
        </vs-col>
      </vs-row>
    </div>

    <!-- Action Buttons -->
    <div class="mt-5 flex w-full gap-5 justify-end">
      <vs-button
        type="border"
        color="primary"
        class="px-8 h-12"
        @click="handleClose"
        :disabled="isBusy"
      >
        Cancel
      </vs-button>
      <vs-button
        type="filled"
        color="primary"
        class="px-8 h-12"
        @click="onHandleSubmit"
        :disabled="isBusy"
      >
        Save Changes
      </vs-button>
    </div>
  </vs-popup>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import SelectWithSearch from '@/components/selectWithSearch'

export default {
  components: {
    SelectWithSearch,
    DataTable
  },
  props: {
    active: {
      type: Boolean,
      default: () => false
    },
    isView: {
      type: Boolean,
      default: () => false
    },
    id: {
      type: String,
      default: () => ''
    },
    rowData: {
      type: Object,
      default: () => null
    }
  },
  data: () => ({
    isBusy: false,
    isStorage: false,
    form: {
      trip_number: 'BEK-2502-068',
      tug_name: null,
      quantity: '4,457.45',
      flf_loading_facility: null,
      product_id: null,
      barge_name: null,
      vessel_agent: null,
      flf_nominate: null,
      blending_scheme: null,
      ratio_first: 50,
      ratio_second: 50
    },
    blendingSchemeOptions: [
      { text: 'Non Blending', value: 'non_blending' },
      { text: 'Blending', value: 'blending' }
    ],
    tripNumberOptions: [],
    // Blending table configuration
    blendingTableHeaders: [
      { key: 'no', label: 'No' },
      { key: 'action', label: 'Action' },
      { key: 'trip_number', label: 'Trip Number', width: '200px' },
      { key: 'tug_name', label: 'Tug Name' },
      { key: 'barge_name', label: 'Barge Name' },
      { key: 'product', label: 'Product' }
    ],
    blendingTableData: []
  }),
  async mounted() {
    await this.getFlfFcNominates()
    await this.getAgencies()
    await this.getLoadingFacilities()
    this.populateTripNumberOptions()
    if (this.id) {
      this.fetchDetail()
    }
    if (this.rowData) {
      await this.populateForm()
    }
  },
  watch: {
    rowData: {
      async handler(newVal) {
        if (newVal) {
          await this.populateForm()
        }
      },
      immediate: true
    },
    active: {
      handler(newVal) {
        if (!newVal) {
          this.resetForm()
        }
      }
    },
    'form.flf_nominate': {
      handler(newVal) {
        this.isStorage = newVal === 'WHS iskandar' || newVal === 7
        if (this.isStorage) {
          this.form.blending_scheme = 'non_blending'
        }
      }
    },
    'form.flf_loading_facility': {
      async handler(newVal) {
        if (newVal) {
          this.form.flf_nominate = null
          await this.getFlfFcNominates()
        }
      }
    },
    'form.blending_scheme': {
      async handler(newVal) {
        if (newVal === 'blending' && this.rowData) {
          await this.populateBlendingTable()
        } else if (newVal === 'non_blending') {
          this.blendingTableData = []
        }
      }
    },
    loadingPlans: {
      handler() {
        this.populateTripNumberOptions()
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.resetForm()
  },
  computed: {
    ...mapGetters({
      flfOptions: 'VesselLineUp/flfFcNominates',
      vesselOptions: 'VesselLineUp/agencies',
      loadingFacilityOptions: 'VesselLineUp/loadingFacilities'
    }),
    loadingPlans() {
      return this.$store.getters['TentativeBargeSchedule/loadingPlans']
    },
    formattedBlendingTableData() {
      return this.blendingTableData || []
    }
  },
  methods: {
    resetForm() {
      this.form = {
        trip_number: '',
        tug_name: null,
        quantity: '',
        flf_loading_facility: null,
        product_id: null,
        barge_name: null,
        vessel_agent: null,
        flf_nominate: null,
        blending_scheme: null,
        ratio_first: 50,
        ratio_second: 50
      }
      this.blendingTableData = []
      this.isBusy = false
    },
    handleClose() {
      this.resetForm()
      this.$emit('close')
    },
    async populateForm() {
      if (!this.rowData) return
      
      const ratio = this.parseBlendingRatio(this.rowData.blending_scheme)
      
      this.form = {
        trip_number: this.rowData.trip_number || '',
        tug_name: this.rowData.tug || null,
        quantity: this.rowData.actual_quantity || '',
        flf_loading_facility: this.findOptionValue(this.loadingFacilityOptions, this.rowData.loading_facility),
        product_id: this.rowData.product_id || null,
        barge_name: this.rowData.barge || null,
        vessel_agent: this.findOptionValue(this.vesselOptions, this.rowData.agency),
        flf_nominate: this.findOptionValue(this.flfOptions, this.rowData.nominate),
        blending_scheme: this.getBlendingSchemeValue(this.rowData.blending_scheme),
        ratio_first: ratio.first,
        ratio_second: ratio.second
      }
      
      if (this.form.blending_scheme === 'blending') {
        await this.populateBlendingTable()
      }
    },
    findOptionValue(options, displayName) {
      if (!displayName || !options || !options.length) return null
      
      const option = options.find(opt => opt.text === displayName)
      return option ? option.value : null
    },
    async populateBlendingTable() {
      try {
        const loadingPlans = this.loadingPlans
        
        if (!loadingPlans || !loadingPlans.loading_plan) {
          const fallbackData = [
            {
              no: 1,
              loading_plan_id: this.findLoadingPlanIdByTripNumber(this.rowData.trip_number) || 0,
              trip_number: this.rowData.trip_number || '',
              tug_name: this.rowData.tug || '',
              barge_name: this.rowData.barge || '',
              product: this.rowData.product_id || ''
            }
          ]

          this.$set(this, 'blendingTableData', fallbackData)
          return
        }
        const currentBlendingGroup = this.findBlendingSchemeGroup(loadingPlans.loading_plan)
        if (currentBlendingGroup) {
          const newData = currentBlendingGroup.map((plan, index) => ({
            no: index + 1,
            loading_plan_id: plan.id || 0,
            trip_number: plan.blu_data?.trip_number || '',
            tug_name: plan.blu_data?.tug || '',
            barge_name: plan.blu_data?.barge || '',
            product: plan.product || ''
          }))
          this.$set(this, 'blendingTableData', newData)
        } else {
          const fallbackData = [
            {
              no: 1,
              loading_plan_id: this.findLoadingPlanIdByTripNumber(this.rowData.trip_number) || 0,
              trip_number: this.rowData.trip_number || '',
              tug_name: this.rowData.tug || '',
              barge_name: this.rowData.barge || '',
              product: this.rowData.product_id || ''
            }
          ]
          this.$set(this, 'blendingTableData', fallbackData)
        }
        this.populateTripNumberOptions()
      } catch (error) {
        const fallbackData = [
          {
            no: 1,
            loading_plan_id: this.findLoadingPlanIdByTripNumber(this.rowData.trip_number) || 0,
            trip_number: this.rowData.trip_number || '',
            tug_name: this.rowData.tug || '',
            barge_name: this.rowData.barge || '',
            product: this.rowData.product_id || ''
          }
        ]
        this.$set(this, 'blendingTableData', fallbackData)
        this.populateTripNumberOptions()
      }
    },
    findBlendingSchemeGroup(loadingPlans) {
      if (!loadingPlans || !this.rowData) return null

      const currentGroup = this.rowData.blending_scheme_group
      if (!currentGroup) return null

      const sameGroupPlans = loadingPlans.filter(plan => 
        plan.blending_scheme_group === currentGroup
      )

      return sameGroupPlans.length ? sameGroupPlans : null
    },
    getBlendingSchemeValue(blendingScheme) {
      if (!blendingScheme) return null
      
      if (blendingScheme.includes('NON-BLENDING')) {
        return 'non_blending'
      } else if (blendingScheme.includes('BLENDING')) {
        return 'blending'
      }
      return null
    },
    parseBlendingRatio(blendingScheme) {
      if (!blendingScheme) return { first: 50, second: 50 }
      
      const ratioMatch = blendingScheme.match(/(\d+):(\d+)/)
      if (ratioMatch) {
        return {
          first: parseInt(ratioMatch[1]),
          second: parseInt(ratioMatch[2])
        }
      }
      
      return { first: 50, second: 50 }
    },
    async getFlfFcNominates() {
      await this.$store.dispatch('VesselLineUp/getFlfFcNominatesByLoadingFacility', this.form.flf_loading_facility)
    },
    async getAgencies() {
      await this.$store.dispatch('VesselLineUp/getAgencies')
    },
    async getLoadingFacilities() {
      await this.$store.dispatch('VesselLineUp/getLoadingFacilities')
    },
    populateTripNumberOptions() {
      const loadingPlans = this.loadingPlans
      let tripNumbers = []
      
      if (loadingPlans && loadingPlans.loading_plan) {
        tripNumbers = [...new Set(
          loadingPlans.loading_plan
            .map(plan => plan.blu_data?.trip_number)
            .filter(tripNumber => tripNumber)
        )]
      }
      
      this.tripNumberOptions = tripNumbers.map(tripNumber => ({
        text: tripNumber,
        value: tripNumber
      }))
    },
    getTripNumberOptionsForRow(rowIndex) {
      if (!this.tripNumberOptions || this.tripNumberOptions.length === 0) {
        return []
      }
      
      const usedTripNumbers = this.blendingTableData
        .filter((row, index) => index !== rowIndex && row.trip_number)
        .map(row => row.trip_number)
      
      return this.tripNumberOptions.filter(option => 
        !usedTripNumbers.includes(option.value)
      )
    },
    populateTripDetailsForRow(rowIndex, tripNumber) {
      const loadingPlans = this.loadingPlans
      
      if (!loadingPlans || !loadingPlans.loading_plan || !tripNumber) return
      
      const matchingPlan = loadingPlans.loading_plan.find(plan => 
        plan.blu_data?.trip_number === tripNumber
      )
      
      if (matchingPlan) {
        const currentRow = this.blendingTableData[rowIndex]
        this.$set(this.blendingTableData, rowIndex, {
          ...currentRow,
          tug_name: matchingPlan.blu_data?.tug || currentRow.tug_name,
          barge_name: matchingPlan.blu_data?.barge || currentRow.barge_name,
          product: matchingPlan.product || currentRow.product,
          loading_plan_id: matchingPlan.id || currentRow.loading_plan_id
        })
      }
    },
    async getLoadingPlans() {
      try {
        this.isLoading = true
        await this.$store.dispatch('TentativeBargeSchedule/fetchTentativeBargeSchedulesLoadingPlans', this.$route.params.id || this.vesselLineUpId)
        this.isLoading = false
      } catch (e) {
        this.isLoading = false
        this.showFailedNotification(this, 'Failed to fetch Tentative Barge Schedule, please try again')
      }
    },
    async onHandleSubmit() {
      try {
        this.isBusy = true
        
        const requiredFields = ['vessel_agent', 'flf_nominate', 'flf_loading_facility']
        const missingField = requiredFields.find(field => !this.form[field])
        
        if (missingField) {
          this.showFailedNotification(this, `${missingField.replace('_', ' ')} is required`)
          return
        }

        if (this.form.blending_scheme === 'blending') {
          if (!this.form.ratio_first || !this.form.ratio_second) {
            this.showFailedNotification(this, 'Ratio fields are required for blending schemes')
            return
          }
          
          const ratioFirst = parseInt(this.form.ratio_first) || 0
          const ratioSecond = parseInt(this.form.ratio_second) || 0
          const totalRatio = ratioFirst + ratioSecond
          
          if (totalRatio !== 100) {
            this.showFailedNotification(this, 'Ratio must equal 100')
            return
          }
        }

        const updateData = {
          agency_id: this.form.vessel_agent || 0,
          loading_facility_id: this.form.flf_loading_facility || 0,
          flf_fc_nominate_id: this.form.flf_nominate || 0,
          blending_scheme_spec: this.form.blending_scheme === 'blending' ? 'Blending' : 'Non-Blending',
          blending_scheme_ratio: this.form.blending_scheme === 'blending' ? `${this.form.ratio_first}:${this.form.ratio_second}` : null,
          blending_scheme_group: this.rowData?.blending_scheme_group || null,
          blu_data: this.form.blending_scheme === 'blending' 
            ? this.blendingTableData.map(row => ({
              loading_plan_id: row.loading_plan_id || 0,
              trip_number: row.trip_number || ''
            }))
            : [{
              loading_plan_id: this.findLoadingPlanIdByTripNumber(this.form.trip_number) || 0,
              trip_number: this.form.trip_number || ''
            }]
        }

        // Submit data using the API
        await this.$store.dispatch('TentativeBargeSchedule/updateLoadingPlan', updateData)

        this.$emit('close')
        this.showSuccessNotification(this, 'TBS record successfully updated')
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to save changes')
      } finally {
        this.isBusy = false
      }
    },
    addBlendingRow() {
      const newRow = {
        no: this.blendingTableData.length + 1,
        loading_plan_id: 0,
        trip_number: '',
        tug_name: '',
        barge_name: '',
        product: ''
      }
      this.blendingTableData.push(newRow)
    },
    removeBlendingRow(row) {
      this.blendingTableData.splice(row.no-1, 1)
      // Update row numbers
      this.blendingTableData.forEach((row, idx) => {
        row.no = idx + 1
      })
    },
    findLoadingPlanIdByTripNumber(tripNumber) {
      if (!tripNumber) return null
      
      const loadingPlans = this.loadingPlans
      
      if (!loadingPlans || !loadingPlans.loading_plan) return null
      
      const matchingPlan = loadingPlans.loading_plan.find(plan => 
        plan.blu_data?.trip_number === tripNumber
      )
      
      return matchingPlan ? matchingPlan.id : null
    }
  }
}
</script>

<style lang="scss" scoped>
.popup {
  /deep/.vs-popup {
    @apply rounded-2xl;
    width: 55rem;

    &--title {
      @apply hidden;
    }

    &--content {
      @apply p-6;
      @apply relative;
      @apply m-0;
      @apply flex;
      @apply flex-col;
      @apply h-full;
      @apply w-full;
      @apply items-center;
      @apply overflow-hidden;

      &-title {
        @apply text-2xl;
        @apply font-semibold;
        @apply text-grey;
        @apply mt-8;
      }
    }

    &--img {
      width: 152px;
    }

    &--close-button {
      @apply cursor-pointer;
      @apply absolute;
      width: 35px;
      top: 1rem;
      right: 1rem;
    }

    &--ok {
      @apply rounded-xl;
      @apply border-2;
      @apply mt-20;
      @apply py-4;
      @apply min-w-button;
      @apply font-semibold;
      height: calc(1rem * 10 / 3);

      &.shadow-secondary {
        -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary), 1);
        box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary), 1);
      }

      &:focus {
        @apply outline-none;
        @apply border-none;
      }
    }

    p {
      @apply text-base;
      @apply text-grey-001;
    }
  }
}
</style>
